import { cn } from '@/shared/utils/utils';
import React from 'react';
import { Target, IndianRupee } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';

interface SalesTargetWidgetProps {
  target?: number;
  achieved?: number;
  expectedClosing?: number;
  /** False when no target has been set for this rep this month — the honest
   *  "not configured" state, distinct from a target of ₹0 achieved. */
  hasTarget?: boolean;
}

export const SalesTargetWidget: React.FC<SalesTargetWidgetProps> = ({
  target = 0,
  achieved = 0,
  expectedClosing = 0,
  hasTarget = false,
}) => {
  const percentage = Math.min(100, Math.round((achieved / (target || 1)) * 100));
  const remaining = Math.max(0, target - achieved);
  const projected = achieved + expectedClosing;
  const projectedPct = Math.min(100, Math.round((projected / (target || 1)) * 100));

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-outline/80 pb-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
          <Target className="h-4 w-4" />
        </div>
        <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
          Target progress
        </span>
      </div>

      {!hasTarget ? (
        <div className="my-4 flex flex-col items-center justify-center gap-1.5 rounded-card-sm border border-dashed border-border bg-surface-variant px-4 py-8 text-center">
          <IndianRupee className="h-5 w-5 text-on-surface-subtle" aria-hidden />
          <p className="text-body-sm font-medium text-on-surface">No sales target set for this month.</p>
          <p className="text-data-xs text-on-surface-variant">Ask your department head to set one in Targets — this tile stays empty rather than showing 0%.</p>
        </div>
      ) : (
        <>
          {/* Body: Radial Gauge + Breakdown */}
          <div className="my-4 flex items-center justify-around gap-4">
            <div className="relative flex h-28 w-28 items-center justify-center shrink-0">
              <svg className="h-full w-full -rotate-90 transform" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r={radius} strokeWidth="8" fill="transparent" className="stroke-surface-container-high" />
                <circle
                  cx="50"
                  cy="50"
                  r={radius}
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  className={cn('transition-all duration-500 ease-out motion-reduce:transition-none',
                    percentage >= 100 ? 'stroke-success' : percentage >= 50 ? 'stroke-primary' : 'stroke-warning')}
                />

              </svg>
              <div className="absolute flex flex-col items-center justify-center text-center">
                <span className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{percentage}%</span>
                <span className="text-data-xs font-semibold uppercase tracking-wider text-on-surface-subtle">Achieved</span>
              </div>
            </div>

            <div className="flex flex-col gap-2 text-body-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-on-surface-subtle">Target</span>
                <span className="font-mono font-semibold tabular-nums text-on-surface">{formatINR(target)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-on-surface-subtle">Achieved</span>
                <span className="font-mono font-semibold tabular-nums text-success-text">{formatINR(achieved)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-on-surface-subtle">Remaining</span>
                <span className="font-mono font-semibold tabular-nums text-warning-text">{formatINR(remaining)}</span>
              </div>
            </div>
          </div>

          {/* Projection Footer */}
          <div className="rounded-lg border border-border bg-surface-variant/70 p-3 text-body-sm">
            <div className="flex items-center justify-between">
              <span className="text-on-surface-subtle">Expected closing pipeline</span>
              <span className="font-mono font-semibold tabular-nums text-on-surface">{formatINR(expectedClosing)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-data-xs">
              <span className="text-on-surface-subtle">Projected Total Achievement:</span>
              <span className="font-semibold text-primary">{projectedPct}% of monthly quota</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
